import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  calculateEarnedSalaryPath,
  getPayrollSummaryDashboardPath,
  getPayrollSummaryDataPath,
} from "../../../../services/config";
import { config } from "../../../../services/configHeader";
import { useGlobalToast } from "../../../../stopOldToastGlobally";
import MonthPicker from "../MonthPicker/index.js";
import CustomPagination from "../../../../components/CustomPagination";

const baseUrl = process.env.REACT_APP_BASE_URI;

const DEFAULT_COLORS = ["#8660F8", "#F59E0B", "#4ADE80", "#3B82F6", "#A855F7", "#EC4899"];

const asArray = (x) => (Array.isArray(x) ? x : x != null ? [x] : []);

const pick = (obj, keys, fallback = "") => {
  if (!obj || typeof obj !== "object") return fallback;
  for (const k of keys) {
    if (obj[k] != null && obj[k] !== "") return obj[k];
  }
  return fallback;
};

const unwrapResponse = (res) => {
  const body = res?.data;
  if (body == null) return null;
  const ok =
    body.status === true ||
    body.status === "true" ||
    body.code === 200 ||
    body.code === "200";
  if (body.data !== undefined && (ok || body.data !== null)) return body.data;
  return body;
};

const noCacheAxiosConfig = () => ({
  ...config,
  headers: {
    ...(config.headers || {}),
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  },
});

const withCacheBust = (qs) => {
  const sep = qs ? "&" : "";
  return `${qs}${sep}_nc=${Date.now()}`;
};

const fmtSummaryMoney = (amount, unit) => {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  if (unit === "L") return `₹${n}L`;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const emptyPS = () => ({
  kpiCards: [],
  payrollTrend: { months: [], values: [] },
  complianceStatus: [],
  criticalAlerts: [],
  statutoryCompliance: [],
  payoutDistribution: [],
  quickInsights: [],
  grossPayrollBreakdown: { labels: [], values: [], colors: [] },
  employeePayrollStatus: [],
  departmentPayrollSummary: [],
});

const EMP_TYPES = [
  { value: "", label: "All Types" },
  { value: "full_time", label: "Full-Time" },
  { value: "contractor", label: "Contractor" },
];

function normalizeStatutoryItem(raw) {
  const title = pick(raw, ["title", "name", "statutoryName", "complianceType"], "—");
  const sub = pick(raw, ["sub", "subtitle", "description", "componentName"], "");
  const amount = pick(raw, ["amount", "totalAmount", "value"], null);
  const due = pick(raw, ["due", "dueDate", "period"], "—");
  const status = pick(raw, ["status", "label"], "—");
  const statusRaw = String(status).toLowerCase();
  let statusColor = "accent";
  if (statusRaw.includes("pend") || statusRaw.includes("draft")) statusColor = "warning";
  else if (statusRaw.includes("filed") || statusRaw.includes("clear") || statusRaw.includes("paid") || statusRaw.includes("complete")) statusColor = "success";
  else if (statusRaw.includes("fail") || statusRaw.includes("overdue")) statusColor = "error";
  const amtStr =
    amount == null || amount === ""
      ? "—"
      : typeof amount === "number"
        ? `₹${amount.toLocaleString("en-IN")}`
        : String(amount);
  return {
    bl: pick(raw, ["bl", "borderColor"], "var(--primary)"),
    icon: pick(raw, ["icon"], "fa-solid fa-file-invoice-dollar"),
    ibg: pick(raw, ["ibg"], "rgba(134,96,248,0.1)"),
    ic: pick(raw, ["ic"], "var(--primary)"),
    title,
    sub,
    amount: amtStr,
    due,
    status,
    statusColor,
  };
}

function normalizeComplianceRow(raw) {
  return {
    icon: pick(raw, ["icon"], "fa-solid fa-shield-halved"),
    ibg: pick(raw, ["ibg"], "rgba(74,222,128,0.1)"),
    ic: pick(raw, ["ic"], "var(--color-success)"),
    title: pick(raw, ["title", "name"], "—"),
    sub: pick(raw, ["sub", "subtitle"], ""),
    label: pick(raw, ["label", "status"], "—"),
    cc: pick(raw, ["cc", "statusColor"], "success"),
  };
}

function normalizeEmployeeRow(raw) {
  const name = pick(raw, ["name", "staffName", "employeeName"], "—");
  const id = pick(raw, ["id", "employeeId", "empCode"], "—");
  const role = pick(raw, ["role", "jobTitle", "designation"], "—");
  const gross = pick(raw, ["gross", "grossPay", "grossSalary", "CostToCompany"], null);
  const deductions = pick(raw, ["deductions", "totalDeduction"], null);
  const net = pick(raw, ["net", "netPay", "netSalary"], null);
  const st = pick(raw, ["status", "payrollStatus"], "—");
  const stl = String(st).toLowerCase();
  let statusColor = "accent";
  if (stl.includes("review") || stl.includes("pend")) statusColor = "warning";
  if (stl.includes("hold") || stl.includes("fail")) statusColor = "error";
  if (stl.includes("ready") || stl.includes("process") || stl.includes("complete")) statusColor = "success";
  const fmt = (v) =>
    v == null || v === ""
      ? "—"
      : typeof v === "number"
        ? `₹${v.toLocaleString("en-IN")}`
        : String(v);
  const initials =
    pick(raw, ["initials"], "") ||
    String(name)
      .split(/\s+/)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  return {
    name,
    id,
    role,
    gross: fmt(gross),
    deductions: fmt(deductions),
    net: fmt(net),
    status: String(st),
    statusColor,
    avatar: raw.avatar || null,
    initials,
    departmentId: String(raw.departmentId ?? raw.department_id ?? ""),
    branchId: String(raw.branchId ?? raw.branch_id ?? ""),
    deptName: pick(raw, ["deptName", "department", "department_name"], ""),
  };
}

function normalizeDepartmentRow(raw) {
  const fmt = (v) =>
    v == null || v === ""
      ? "—"
      : typeof v === "number"
        ? `₹${v.toLocaleString("en-IN")}`
        : String(v);
  return {
    icon: pick(raw, ["icon"], "fa-solid fa-building"),
    ibg: pick(raw, ["ibg"], "rgba(134,96,248,0.1)"),
    ic: pick(raw, ["ic"], "var(--primary)"),
    name: pick(raw, ["name", "department", "department_name"], "—"),
    employeeCount: pick(raw, ["employeeCount", "employees", "count"], "—"),
    gross: fmt(pick(raw, ["gross", "grossPayroll", "grossSalary"], null)),
    benefits: fmt(pick(raw, ["benefits", "benefitsAmount"], null)),
    deductions: fmt(pick(raw, ["deductions", "deductionTotal"], null)),
    net: fmt(pick(raw, ["net", "netPayroll", "netSalary"], null)),
    status: pick(raw, ["status"], "—"),
    statusColor: pick(raw, ["statusColor"], "success"),
    pctOfTotal: (() => {
      const p = pick(raw, ["pctOfTotal", "percentage", "pct"], null);
      if (p == null || p === "") return "—";
      if (typeof p === "number") return `${p}%`;
      return String(p);
    })(),
  };
}

function normalizeKpi(raw) {
  return {
    icon: pick(raw, ["icon"], "fa-solid fa-wallet"),
    iconColor: pick(raw, ["iconColor"], "var(--primary)"),
    glow: pick(raw, ["glow"], "rgba(134,96,248,0.12)"),
    label: pick(raw, ["label"], "—"),
    badge: pick(raw, ["badge"], ""),
    badgeColor: pick(raw, ["badgeColor"], "accent"),
    value: pick(raw, ["value"], "—"),
    sub: pick(raw, ["sub"], ""),
  };
}

function normalizePayout(raw) {
  return {
    icon: pick(raw, ["icon"], "fa-solid fa-check-circle"),
    ic: pick(raw, ["ic"], "var(--color-success)"),
    ibg: pick(raw, ["ibg"], "rgba(74,222,128,0.1)"),
    grad: pick(raw, ["grad"], "linear-gradient(to right,#4ADE80,#86efac)"),
    label: pick(raw, ["label"], "—"),
    sub: pick(raw, ["sub"], ""),
    count: pick(raw, ["count"], "—"),
    pl: pick(raw, ["pl"], ""),
    pct: Number(pick(raw, ["pct", "percent"], 0)) || 0,
  };
}

function normalizeInsight(raw) {
  return {
    icon: pick(raw, ["icon"], "fa-solid fa-bolt"),
    grad: pick(raw, ["grad"], "linear-gradient(135deg,var(--primary),#7c3aed)"),
    shadow: pick(raw, ["shadow"], "rgba(134,96,248,0.2)"),
    label: pick(raw, ["label"], "—"),
    value: pick(raw, ["value"], "—"),
    trend: pick(raw, ["trend"], ""),
    tc: pick(raw, ["tc", "trendColor"], "var(--text-muted)"),
  };
}

function normalizeAlert(raw) {
  return {
    icon: pick(raw, ["icon"], "fa-solid fa-circle-exclamation"),
    ic: pick(raw, ["ic"], "var(--color-warning)"),
    bl: pick(raw, ["bl"], "var(--color-warning)"),
    title: pick(raw, ["title"], "—"),
    sub: pick(raw, ["sub"], ""),
  };
}

function normalizeBreakdown(raw) {
  if (!raw || typeof raw !== "object") return { labels: [], values: [], colors: [] };
  if (Array.isArray(raw.labels) && Array.isArray(raw.values)) {
    const colors = Array.isArray(raw.colors) ? raw.colors : DEFAULT_COLORS;
    return {
      labels: raw.labels,
      values: raw.values.map((v) => Number(v) || 0),
      colors: colors.slice(0, raw.labels.length),
    };
  }
  const list = asArray(raw.items || raw.rows);
  if (list.length && typeof list[0] === "object") {
    const labels = list.map((r) => pick(r, ["label", "name", "component"], "?"));
    const values = list.map((r) => Number(pick(r, ["value", "amount", "monthlyAmount"], 0)) || 0);
    return { labels, values, colors: DEFAULT_COLORS.slice(0, labels.length) };
  }
  return { labels: [], values: [], colors: [] };
}

function normalizeTrend(raw) {
  if (!raw || typeof raw !== "object") return { months: [], values: [] };
  if (Array.isArray(raw.months) && Array.isArray(raw.values)) {
    return { months: raw.months, values: raw.values.map((v) => Number(v) || 0) };
  }
  const list = asArray(raw.series || raw.points || raw.data);
  if (list.length && typeof list[0] === "object") {
    return {
      months: list.map((r) => pick(r, ["month", "label", "name"], "")),
      values: list.map((r) => Number(pick(r, ["value", "amount"], 0)) || 0),
    };
  }
  return { months: [], values: [] };
}

function mergePayrollSummaryPayload(dashboard, earning, statutoryRaw) {
  const d = Array.isArray(dashboard)
    ? { employeePayrollStatus: dashboard }
    : dashboard && typeof dashboard === "object" && !Array.isArray(dashboard)
      ? dashboard
      : {};
  const e = Array.isArray(earning)
    ? { employeePayrollStatus: earning }
    : earning && typeof earning === "object" && !Array.isArray(earning)
      ? earning
      : {};

  let statutoryList = [];
  if (Array.isArray(statutoryRaw)) {
    statutoryList = statutoryRaw;
  } else if (statutoryRaw && typeof statutoryRaw === "object") {
    statutoryList = asArray(
      statutoryRaw.statutoryCompliance ??
        statutoryRaw.statutoryDetails ??
        statutoryRaw.list ??
        statutoryRaw.data
    );
  }

  const fromDashboardStat = asArray(d.statutoryCompliance).map(normalizeStatutoryItem);
  const statutoryCompliance =
    statutoryList.length > 0 ? statutoryList.map(normalizeStatutoryItem) : fromDashboardStat;

  const earningBreakdown = normalizeBreakdown(
    e.grossPayrollBreakdown || e.breakdown || e.payrollBreakdown
  );
  const dashboardBreakdown = normalizeBreakdown(d.grossPayrollBreakdown);
  const grossPayrollBreakdown =
    dashboardBreakdown.labels.length > 0
      ? dashboardBreakdown
      : earningBreakdown.labels.length > 0
        ? earningBreakdown
        : { labels: [], values: [], colors: [] };

  const kpiFrom = asArray(d.kpiCards?.length ? d.kpiCards : e.kpiCards);

  return {
    kpiCards: kpiFrom.map(normalizeKpi),
    payrollTrend: normalizeTrend(d.payrollTrend || e.payrollTrend),
    complianceStatus: asArray(d.complianceStatus || e.complianceStatus).map(normalizeComplianceRow),
    criticalAlerts: asArray(d.criticalAlerts || e.criticalAlerts).map(normalizeAlert),
    statutoryCompliance,
    payoutDistribution: asArray(d.payoutDistribution || e.payoutDistribution).map(normalizePayout),
    quickInsights: asArray(d.quickInsights || e.quickInsights).map(normalizeInsight),
    grossPayrollBreakdown,
    employeePayrollStatus: asArray(
      d.employeePayrollStatus || e.employeePayrollStatus || e.employees || d.employees
    ).map(normalizeEmployeeRow),
    departmentPayrollSummary: asArray(
      d.departmentPayrollSummary || e.departmentPayrollSummary || d.departments || e.departments
    ).map(normalizeDepartmentRow),
  };
}

/** API: GET admin/getPayrollSummaryData — maps summary payload into dashboard UI shape */
function mapGetPayrollSummaryDataPayload(raw) {
  if (!raw || typeof raw !== "object") return emptyPS();
  const data = raw.summary || raw.employeePayrollList ? raw : raw.data || raw;

  const s = data.summary || {};
  const totalPayrollValue =
    s.totalPayroll?.amount ??
    s.totalPayroll ??
    data.OverallCostToCompany ??
    data.overallCostToCompany ??
    0;
  const grossValue =
    s.gross?.amount ??
    s.gross ??
    data.TotalGross ??
    data.totalGross ??
    data.totalGrossSalary ??
    data.OverAllGrossSalary ??
    0;
  const netValue =
    s.net?.amount ??
    s.net ??
    data.TotalNet ??
    data.totalNet ??
    data.totalNetSalary ??
    data.OverAllNetSalary ??
    0;
  const kpiCards = [];

  // Always show all 6 cards — use 0/fallback if API doesn't return the field
  kpiCards.push(normalizeKpi({
    icon: "fa-solid fa-wallet",
    iconColor: "var(--primary)",
    glow: "rgba(134,96,248,0.12)",
    label: "Total payroll (CTC)",
    badge: s.totalPayroll?.percentage || "",
    badgeColor: "accent",
    value: fmtSummaryMoney(totalPayrollValue),
    sub: s.totalPayroll?.vsLastMonth || "Overall cost to company",
  }));

  kpiCards.push(normalizeKpi({
    icon: "fa-solid fa-money-bill-trend-up",
    iconColor: "var(--color-success)",
    glow: "rgba(74,222,128,0.10)",
    label: "Total gross",
    badge: s.gross?.percentage || "",
    badgeColor: "success",
    value: fmtSummaryMoney(grossValue),
    sub: s.gross?.label || "Before deductions",
  }));

  kpiCards.push(normalizeKpi({
    icon: "fa-solid fa-hand-holding-dollar",
    iconColor: "#22d3ee",
    glow: "rgba(34,211,238,0.10)",
    label: "Total net",
    badge: s.net?.percentage || "",
    badgeColor: "success",
    value: fmtSummaryMoney(netValue),
    sub: s.net?.label || "After all deductions",
  }));

  kpiCards.push(normalizeKpi({
    icon: "fa-solid fa-users",
    iconColor: "var(--color-warning)",
    glow: "rgba(245,158,11,0.10)",
    label: "Employees",
    badge: s.activeEmployees?.badge || "",
    badgeColor: "success",
    value: String(s.activeEmployees?.count ?? 0),
    sub: `${s.activeEmployees?.processed ?? 0} payroll processed`,
  }));

  kpiCards.push(normalizeKpi({
    icon: "fa-solid fa-scale-balanced",
    iconColor: "var(--color-danger)",
    glow: "rgba(239,68,68,0.10)",
    label: "Statutory dues",
    badge: s.statutoryDues?.percentage || "",
    badgeColor: s.statutoryDues?.requiresManualReview ? "warning" : "accent",
    value: fmtSummaryMoney(s.statutoryDues?.amount ?? 0),
    sub: s.statutoryDues?.label || "Taxes, PF, Insurance",
  }));

  kpiCards.push(normalizeKpi({
    icon: "fa-solid fa-triangle-exclamation",
    iconColor: "#f59e0b",
    glow: "rgba(245,158,11,0.10)",
    label: "Exceptions",
    badge: s.exceptions?.count ? `${s.exceptions.count}` : "",
    badgeColor: "warning",
    value: String(s.exceptions?.count ?? 0),
    sub: s.exceptions?.label || "Requires manual review",
  }));

  const complianceStatus = asArray(data.complianceStatus).map((row) =>
    normalizeComplianceRow({
      title: row.name || row.title,
      sub: row.due || row.sub || "",
      label: row.amount || row.status || "—",
      cc: String(row.status || "").toLowerCase().includes("pend") ? "warning" : "success",
    })
  );

  const criticalAlerts = asArray(data.alerts).map((row) =>
    normalizeAlert({
      title: row.title,
      sub: row.description || row.sub || "",
    })
  );

  const payoutDistribution = [];
  const ps = data.payoutStatus || {};
  if (ps.directDeposits) {
    payoutDistribution.push(
      normalizePayout({
        icon: "fa-solid fa-circle-check",
        ic: "var(--color-success)",
        ibg: "rgba(74,222,128,0.1)",
        grad: "linear-gradient(to right,#4ADE80,#86efac)",
        label: "Direct Deposits",
        sub: "Processed payments",
        count: ps.directDeposits.processed ?? ps.directDeposits.total,
        pl: `${ps.directDeposits.percentage ?? 0}%`,
        pct: parseFloat(ps.directDeposits.percentage) || 0,
      })
    );
  }
  if (ps.pendingVerification) {
    payoutDistribution.push(
      normalizePayout({
        icon: "fa-solid fa-hourglass-half",
        ic: "var(--color-warning)",
        ibg: "rgba(245,158,11,0.1)",
        grad: "linear-gradient(to right,#F59E0B,#fbbf24)",
        label: "Pending Verification",
        sub: "Awaiting confirmation",
        count: ps.pendingVerification.count ?? 0,
        pl: `${ps.pendingVerification.percentage ?? 0}%`,
        pct: parseFloat(ps.pendingVerification.percentage) || 0,
      })
    );
  }
  if (ps.failedTransactions) {
    payoutDistribution.push(
      normalizePayout({
        icon: "fa-solid fa-circle-xmark",
        ic: "var(--color-danger)",
        ibg: "rgba(239,68,68,0.1)",
        grad: "linear-gradient(to right,#EF4444,#f87171)",
        label: "Failed Transactions",
        sub: "Requires attention",
        count: ps.failedTransactions.count ?? 0,
        pl: `${ps.failedTransactions.percentage ?? 0}%`,
        pct: parseFloat(ps.failedTransactions.percentage) || 0,
      })
    );
  }

  const quickInsights = [];
  const kpis = data.kpis || {};
  if (kpis.avgProcessingTime) {
    quickInsights.push(
      normalizeInsight({
        icon: "fa-solid fa-clock",
        grad: "linear-gradient(135deg,var(--primary),#7c3aed)",
        shadow: "rgba(134,96,248,0.2)",
        label: "Avg. Processing Time",
        value: `${kpis.avgProcessingTime.hours ?? 0}h`,
        trend: kpis.avgProcessingTime.trend || "",
      })
    );
  }
  if (kpis.complianceScore) {
    quickInsights.push(
      normalizeInsight({
        icon: "fa-solid fa-shield-halved",
        grad: "linear-gradient(135deg,var(--color-success),#16a34a)",
        shadow: "rgba(74,222,128,0.2)",
        label: "Compliance Score",
        value: String(kpis.complianceScore.score ?? 0),
        trend: kpis.complianceScore.label || "",
      })
    );
  }
  if (kpis.overtimeCost) {
    quickInsights.push(
      normalizeInsight({
        icon: "fa-solid fa-business-time",
        grad: "linear-gradient(135deg,var(--color-warning),#ea580c)",
        shadow: "rgba(245,158,11,0.2)",
        label: "Overtime Cost",
        value: fmtSummaryMoney(kpis.overtimeCost.amount, kpis.overtimeCost.unit),
        trend: "This month",
      })
    );
  }

  const departmentPayrollSummary = asArray(data.departmentBreakdown).map((row) =>
    normalizeDepartmentRow({
      name: row.department || row.name,
      gross: fmtSummaryMoney(row.amount),
      pctOfTotal: row.percentage,
      employeeCount: row.employeeCount ?? "—",
    })
  );

  const employeePayrollStatus = asArray(data.employeePayrollList).map((row) =>
    normalizeEmployeeRow({
      ...row,
      name: row.name || row.staffName || "—",
    })
  );

  return {
    kpiCards,
    payrollTrend: normalizeTrend(data.trend),
    complianceStatus,
    criticalAlerts,
    statutoryCompliance: [],
    payoutDistribution,
    quickInsights,
    grossPayrollBreakdown: normalizeBreakdown(data.grossPayrollBreakdown),
    employeePayrollStatus,
    departmentPayrollSummary,
  };
}

function mapRunPayrollKpiPayload(raw) {
  if (!raw || typeof raw !== "object") return { kpiCards: [] };
  const data = raw.summary || raw.data || raw;
  const s = data.summary || data || {};
  const occ = s.OverallCostToCompany ?? s.overallCostToCompany;
  const gross =
    s.TotalGross ?? s.totalGross ?? s.totalGrossSalary ?? s.OverAllGrossSalary;
  const net = s.TotalNet ?? s.totalNet ?? s.totalNetSalary ?? s.OverAllNetSalary;

  return {
    kpiCards: [
      normalizeKpi({
        icon: "fa-solid fa-wallet",
        iconColor: "var(--primary)",
        glow: "rgba(134,96,248,0.12)",
        label: "Total payroll (CTC)",
        badge: "",
        badgeColor: "accent",
        value: occ != null && occ !== "" ? fmtSummaryMoney(occ) : "—",
        sub: "From calculateEarnedSalary summary",
      }),
      normalizeKpi({
        icon: "fa-solid fa-money-bill-trend-up",
        iconColor: "var(--color-success)",
        glow: "rgba(74,222,128,0.10)",
        label: "Total gross",
        badge: "",
        badgeColor: "success",
        value: gross != null && gross !== "" ? fmtSummaryMoney(gross) : "—",
        sub: "Before deductions (if provided)",
      }),
      normalizeKpi({
        icon: "fa-solid fa-hand-holding-dollar",
        iconColor: "#22d3ee",
        glow: "rgba(34,211,238,0.10)",
        label: "Total net",
        badge: "",
        badgeColor: "success",
        value: net != null && net !== "" ? fmtSummaryMoney(net) : "—",
        sub: "After deductions (if provided)",
      }),
    ],
  };
}

// Replace overlayPayrollSummary in PayrollSummary frontend:
function overlayPayrollSummary(primary, secondary, runPayroll) {
  const hasArr = (a) => Array.isArray(a) && a.length > 0;
  const hasTrend = (t) => Array.isArray(t?.months) && t.months.length > 0;
  const hasBreakdown = (b) => Array.isArray(b?.labels) && b.labels.length > 0;

  const baseKpis = hasArr(secondary.kpiCards)
    ? secondary.kpiCards
    : hasArr(primary.kpiCards)
      ? primary.kpiCards
      : [];
  const mergedKpis = hasArr(runPayroll?.kpiCards)
    ? [...runPayroll.kpiCards, ...baseKpis.slice(runPayroll.kpiCards.length)]
    : baseKpis;

  return {
    kpiCards: mergedKpis,
    payrollTrend: hasTrend(primary.payrollTrend) ? primary.payrollTrend : secondary.payrollTrend,
    complianceStatus: hasArr(primary.complianceStatus)
      ? primary.complianceStatus
      : secondary.complianceStatus,
    criticalAlerts: hasArr(primary.criticalAlerts) ? primary.criticalAlerts : secondary.criticalAlerts,
    statutoryCompliance: hasArr(primary.statutoryCompliance)
      ? primary.statutoryCompliance
      : secondary.statutoryCompliance,
    payoutDistribution: hasArr(primary.payoutDistribution)
      ? primary.payoutDistribution
      : secondary.payoutDistribution,
    quickInsights: hasArr(primary.quickInsights) ? primary.quickInsights : secondary.quickInsights,
    grossPayrollBreakdown: hasBreakdown(primary.grossPayrollBreakdown)
      ? primary.grossPayrollBreakdown
      : secondary.grossPayrollBreakdown,
    employeePayrollStatus: hasArr(primary.employeePayrollStatus)
      ? primary.employeePayrollStatus
      : secondary.employeePayrollStatus,
    departmentPayrollSummary: hasArr(primary.departmentPayrollSummary)
      ? primary.departmentPayrollSummary
      : secondary.departmentPayrollSummary,
  };
}

const PayrollSummary = ({ onNavigate }) => {
  const customToast = useGlobalToast();
  const branchList = useSelector((s) => s.branch?.brnGetData || []);
  const departmentList = useSelector((s) => s.department?.depGetData || []);
  const user = JSON.parse(localStorage.getItem("user_data") || "{}") || {};

  const trendChartRef = useRef(null);
  const breakdownChartRef = useRef(null);

  const [PS, setPS] = useState(() => emptyPS());
  const [loading, setLoading] = useState(false);

  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [filterMonth, setFilterMonth] = useState(defaultMonth);
  const [filterBranch, setFilterBranch] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterSearch, setFilterSearch] = useState("");

  const monthLabel = useMemo(() => {
    const [y, m] = filterMonth.split("-");
    if (!y || !m) return "";
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
  }, [filterMonth]);

  const filteredEmployees = useMemo(() => {
    let rows = PS.employeePayrollStatus;
    const q = filterSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          String(r.name).toLowerCase().includes(q) ||
          String(r.id).toLowerCase().includes(q) ||
          String(r.role).toLowerCase().includes(q) ||
          String(r.deptName || "").toLowerCase().includes(q)
      );
    }
    if (filterDept) {
      const deptName = (
        departmentList.find((d) => String(d.departmentId) === filterDept) || {}
      ).department_name;
      rows = rows.filter(
        (r) =>
          String(r.departmentId) === filterDept ||
          (deptName && String(r.deptName || r.role || "").toLowerCase().includes(String(deptName).toLowerCase()))
      );
    }
    if (filterBranch) {
      rows = rows.filter((r) => !r.branchId || String(r.branchId) === filterBranch);
    }
    return rows;
  }, [PS.employeePayrollStatus, filterSearch, filterDept, filterBranch, departmentList]);

  const filteredDepts = useMemo(() => {
    let rows = PS.departmentPayrollSummary;
    if (filterDept) {
      const deptName = (
        departmentList.find((d) => String(d.departmentId) === filterDept) || {}
      ).department_name;
      rows = rows.filter(
        (r) =>
          String(r.name).toLowerCase().includes(String(filterDept).toLowerCase()) ||
          (deptName && String(r.name).toLowerCase().includes(String(deptName).toLowerCase()))
      );
    }
    if (filterSearch.trim()) {
      const q = filterSearch.trim().toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    }
    return rows;
  }, [PS.departmentPayrollSummary, filterDept, filterSearch, departmentList]);

  const filteredStatutory = useMemo(() => {
    if (!filterSearch.trim()) return PS.statutoryCompliance;
    const q = filterSearch.trim().toLowerCase();
    return PS.statutoryCompliance.filter(
      (s) =>
        String(s.title).toLowerCase().includes(q) || String(s.sub || "").toLowerCase().includes(q)
    );
  }, [PS.statutoryCompliance, filterSearch]);

  const clearFilters = () => {
    setFilterMonth(defaultMonth);
    setFilterBranch("");
    setFilterDept("");
    setFilterType("");
    setFilterSearch("");
    setEmpPage(0);
    setDeptPage(0);
  };

  const hasActiveFilter =
    filterBranch || filterDept || filterType || filterSearch || filterMonth !== defaultMonth;

  const [empPage, setEmpPage] = useState(0);
  const [empPageSize, setEmpPageSize] = useState(10);
  const [deptPage, setDeptPage] = useState(0);
  const [deptPageSize, setDeptPageSize] = useState(10);

  useEffect(() => {
    setEmpPage(0);
  }, [filterSearch, filterDept, filterBranch, filterType]);
  useEffect(() => {
    setDeptPage(0);
  }, [filterSearch, filterDept]);

  const pagedEmployees = useMemo(() => {
    const start = empPage * empPageSize;
    return filteredEmployees.slice(start, start + empPageSize);
  }, [filteredEmployees, empPage, empPageSize]);

  const pagedDepts = useMemo(() => {
    const start = deptPage * deptPageSize;
    return filteredDepts.slice(start, start + deptPageSize);
  }, [filteredDepts, deptPage, deptPageSize]);

  const loadPayrollSummary = useCallback(async () => {
    if (!user.user_id || !baseUrl) return;
    setLoading(true);
    const qs = new URLSearchParams({
      userId: String(user.user_id),
      selectedDate: `${filterMonth}-01`,
    });
    if (filterBranch) qs.set("branchId", filterBranch);
    if (filterDept) qs.set("departmentId", filterDept);
    if (filterType) qs.set("employeeType", filterType);
    const query = withCacheBust(qs.toString());
    const axiosCfg = noCacheAxiosConfig();
    const runPayrollBody = {
      userId: String(user.user_id),
      selectedDate: `${filterMonth}-01`,
      branchId: filterBranch || null,
      departmentId: filterDept || null,
      shiftId: null,
    };

    try {
      const results = await Promise.allSettled([
        axios.get(`${baseUrl}${getPayrollSummaryDashboardPath}?${query}`, axiosCfg),
        axios.get(`${baseUrl}${getPayrollSummaryDataPath}?${query}`, axiosCfg),
        axios.post(`${baseUrl}${calculateEarnedSalaryPath}`, runPayrollBody, config),
      ]);

      const dash =
        results[0].status === "fulfilled" ? unwrapResponse(results[0].value) : null;
      const summaryData =
        results[1].status === "fulfilled" ? unwrapResponse(results[1].value) : null;
      const runPayrollData =
        results[2].status === "fulfilled" ? unwrapResponse(results[2].value) : null;

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length === results.length) {
        const err = failed[0].reason;
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          "Failed to load payroll summary";
        throw new Error(msg);
      }
      if (failed.length === 1) {
        customToast.error("One payroll summary request failed; showing partial data.");
      }

      const fromDashboard = mergePayrollSummaryPayload(dash, null, null);
      const fromSummary = mapGetPayrollSummaryDataPayload(summaryData);
      const fromRunPayroll = mapRunPayrollKpiPayload(runPayrollData);
      const merged = overlayPayrollSummary(fromDashboard, fromSummary, fromRunPayroll);
      setPS(merged);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        err?.message ||
        "Failed to load payroll summary";
      customToast.error(msg);
      setPS(emptyPS());
    } finally {
      setLoading(false);
    }
  }, [user.user_id, filterMonth, filterBranch, filterDept, filterType, customToast]);

  useEffect(() => {
    loadPayrollSummary();
  }, [loadPayrollSummary]);

  // Function to get CSS variable values
  const cssVar = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "";

  const initTrendChart = () => {
    if (!window.Plotly || !trendChartRef.current) return;

    const primary = cssVar("--primary") || "#8660F8";
    const muted = cssVar("--text-muted") || "#A9A3C9";
    const bg = cssVar("--pay-surface") || "#ffffff";
    const months = PS.payrollTrend?.months || [];
    const values = PS.payrollTrend?.values || [];

    if (!months.length) {
      window.Plotly.purge(trendChartRef.current);
      window.Plotly.newPlot(
        trendChartRef.current,
        [],
        {
          margin: { t: 10, r: 10, b: 30, l: 40 },
          paper_bgcolor: "transparent",
          plot_bgcolor: "transparent",
          annotations: [
            {
              text: "No trend data for this period",
              xref: "paper",
              yref: "paper",
              x: 0.5,
              y: 0.5,
              showarrow: false,
              font: { family: "Outfit, sans-serif", size: 13, color: muted },
            },
          ],
        },
        { responsive: true, displayModeBar: false }
      );
      return;
    }

    const data = [
      {
        x: months,
        y: values,
        type: "scatter",
        mode: "lines",
        fill: "tozeroy",
        line: { color: primary, width: 3, shape: "spline", smoothing: 1.3 },
        fillcolor: "rgba(134,96,248,0.08)",
        marker: { size: 6, color: bg, line: { color: primary, width: 2 } },
      },
    ];

    const layout = {
      margin: { t: 10, r: 10, b: 30, l: 40 },
      plot_bgcolor: "transparent",
      paper_bgcolor: "transparent",
      xaxis: {
        showgrid: false,
        color: muted,
        tickfont: { family: "Outfit, sans-serif", size: 11 },
      },
      yaxis: {
        showgrid: true,
        gridcolor: "rgba(128,128,128,0.1)",
        color: muted,
        tickprefix: "₹",
        ticksuffix: "Cr",
        tickfont: { family: "Outfit, sans-serif", size: 11 },
      },
      hovermode: "x unified",
      showlegend: false,
    };

    window.Plotly.newPlot(trendChartRef.current, data, layout, { responsive: true, displayModeBar: false });
  };

  const initBreakdownChart = () => {
    if (!window.Plotly || !breakdownChartRef.current) return;

    const muted = cssVar("--text-muted") || "#A9A3C9";
    const textPrimary = cssVar("--text-primary") || "#E3E2EB";
    const labels = PS.grossPayrollBreakdown?.labels || [];
    const values = PS.grossPayrollBreakdown?.values || [];
    const colors = PS.grossPayrollBreakdown?.colors || [];

    if (!labels.length) {
      window.Plotly.purge(breakdownChartRef.current);
      window.Plotly.newPlot(
        breakdownChartRef.current,
        [],
        {
          margin: { t: 20, r: 20, b: 20, l: 20 },
          paper_bgcolor: "transparent",
          plot_bgcolor: "transparent",
          annotations: [
            {
              text: "No earning breakdown for this period",
              xref: "paper",
              yref: "paper",
              x: 0.5,
              y: 0.5,
              showarrow: false,
              font: { family: "Outfit, sans-serif", size: 13, color: muted },
            },
          ],
        },
        { responsive: true, displayModeBar: false }
      );
      return;
    }

    const sum = values.reduce((a, b) => a + (Number(b) || 0), 0);
    const totalLabel =
      sum >= 1e7
        ? `₹${(sum / 1e7).toFixed(2)}Cr`
        : sum >= 1e5
          ? `₹${(sum / 1e5).toFixed(2)}L`
          : `₹${sum.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

    const data = [
      {
        values,
        labels,
        type: "pie",
        hole: 0.5,
        marker: { colors: colors.length ? colors : DEFAULT_COLORS },
        textinfo: "label+percent",
        textfont: { family: "Outfit, sans-serif", size: 12, color: "#FFFFFF" },
        hovertemplate: "<b>%{label}</b><br>₹%{value:,.0f}<br>%{percent}<extra></extra>",
      },
    ];

    const layout = {
      margin: { t: 20, r: 20, b: 20, l: 20 },
      plot_bgcolor: "transparent",
      paper_bgcolor: "transparent",
      showlegend: true,
      legend: {
        orientation: "h",
        y: -0.1,
        x: 0.5,
        xanchor: "center",
        font: { family: "Outfit, sans-serif", size: 11, color: muted },
      },
      annotations: [
        {
          text: `<b>${totalLabel}</b><br><span style='font-size:11px;font-family:Outfit,sans-serif'>Total</span>`,
          x: 0.5,
          y: 0.5,
          font: { size: 13, family: "Outfit, sans-serif", color: textPrimary },
          showarrow: false,
        },
      ],
    };

    window.Plotly.newPlot(breakdownChartRef.current, data, layout, { responsive: true, displayModeBar: false });
  };

  // Load Plotly once, then draw charts (also redraw when PS updates)
  useEffect(() => {
    const loadScript = (src, id) =>
      new Promise((resolve) => {
        if (document.getElementById(id)) {
          resolve();
          return;
        }
        const s = document.createElement("script");
        s.src = src;
        s.id = id;
        s.onload = resolve;
        document.head.appendChild(s);
      });

    const runCharts = () => {
      if (window.Plotly && trendChartRef.current && breakdownChartRef.current) {
        initTrendChart();
        initBreakdownChart();
      }
    };

    if (!document.getElementById("ps-plotly-js")) {
      loadScript("https://cdn.plot.ly/plotly-3.1.1.min.js", "ps-plotly-js").then(runCharts);
    } else {
      runCharts();
    }
  }, []);

  useEffect(() => {
    if (!window.Plotly || !trendChartRef.current || !breakdownChartRef.current) return;
    initTrendChart();
    initBreakdownChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- charts read latest PS from closure
  }, [PS]);

  // Re-initialize charts when theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (window.Plotly && trendChartRef.current && breakdownChartRef.current) {
        initTrendChart();
        initBreakdownChart();
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "class"],
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PS]);

  // ── Shared table style helpers ───────────────────────────────────────────
  const thStyle = (alignRight = false) => ({
    padding: "10px 18px",
    fontWeight: 600,
    fontSize: 10,
    fontFamily: "var(--font, 'Outfit', sans-serif)",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: "var(--text-muted)",
    textAlign: alignRight ? "right" : "left",
    whiteSpace: "nowrap",
  });

  const tdStyle = (extra = {}) => ({
    padding: "11px 18px",
    fontSize: 12,
    fontFamily: "var(--font, 'Outfit', sans-serif)",
    color: "var(--text-primary)",
    verticalAlign: "middle",
    ...extra,
  });

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: "var(--font, 'Outfit', sans-serif)",
    fontSize: 12,
  };

  // ── Status chip ──────────────────────────────────────────────────────────
  const Chip = ({ label, cc }) => {
    const map = {
      success: { bg: "rgba(74,222,128,0.12)",  color: "var(--color-success)", border: "rgba(74,222,128,0.25)"  },
      warning: { bg: "rgba(245,158,11,0.12)",  color: "var(--color-warning)", border: "rgba(245,158,11,0.25)"  },
      error:   { bg: "rgba(239,68,68,0.12)",   color: "var(--color-danger)",  border: "rgba(239,68,68,0.25)"   },
      accent:  { bg: "rgba(134,96,248,0.12)",  color: "var(--primary)",       border: "rgba(134,96,248,0.25)"  },
    };
    const t = map[cc] || map.accent;
    return (
      <span style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}`, borderRadius: "9999px", padding: "3px 9px", fontSize: 10, fontWeight: 600, fontFamily: "var(--font, 'Outfit', sans-serif)", letterSpacing: "0.02em", display: "inline-block" }}>
        {label}
      </span>
    );
  };

  // ── Ghost button ─────────────────────────────────────────────────────────
  const GhostBtn = ({ children, onClick, disabled }) => (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        background: "transparent",
        color: "var(--text-muted)",
        padding: "6px 14px",
        borderRadius: 20,
        fontWeight: 500,
        fontSize: 12,
        fontFamily: "var(--font, 'Outfit', sans-serif)",
        border: "1px solid var(--border)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) => {
        if (e.currentTarget.disabled) return;
        e.currentTarget.style.background = "var(--pay-surface-2)";
        e.currentTarget.style.color = "var(--text-primary)";
        e.currentTarget.style.borderColor = "var(--primary)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = "var(--text-muted)";
        e.currentTarget.style.borderColor = "var(--border)";
      }}
    >
      {children}
    </button>
  );

  // ── Row hover handlers ───────────────────────────────────────────────────
  const rowHoverIn  = e => { e.currentTarget.style.background = "rgba(134,96,248,0.04)"; };
  const rowHoverOut = e => { e.currentTarget.style.background = "transparent"; };

  return (
    <div style={{ fontFamily: "var(--font, 'Outfit', sans-serif)", minHeight: "100vh" }}>
      <main style={{ maxWidth: 1600, margin: "0 auto", padding: "32px", display: "flex", flexDirection: "column", gap: 32 }}>

        {/* ── Page Header ── */}
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24 }}>
          <div>
            <h1 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 6px", color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Payroll Summary</h1>
            <p style={{ fontSize: 13, margin: 0, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>
              {loading
                ? "Loading summary from the server…"
                : `Review, analyze, and finalize payroll for ${monthLabel || filterMonth}.`}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button style={{ background: "transparent", color: "var(--text-muted)", padding: "8px 20px", borderRadius: 24, fontWeight: 500, fontSize: 13, fontFamily: "var(--font, 'Outfit', sans-serif)", border: "1px solid var(--border)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--pay-surface-2)"; e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.borderColor = "var(--primary)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border)"; }}>
              <i className="fa-solid fa-download" style={{ fontSize: 12 }} /> Export Summary
            </button>
            <button
              onClick={() => onNavigate?.("run-payroll")}
              style={{ background: "var(--primary)", color: "#fff", padding: "8px 20px", borderRadius: 24, fontWeight: 600, fontSize: 13, fontFamily: "var(--font, 'Outfit', sans-serif)", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--primary-hover)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "var(--primary)"; e.currentTarget.style.transform = "translateY(0)"; }}>
              <i className="fa-solid fa-play" style={{ fontSize: 12 }} /> Run Payroll
            </button>
          </div>
        </header>

        {/* ── Filter Bar ── */}
        <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>

          {/* Month */}
          <div style={{ flex: "1 1 160px" }}>
            <MonthPicker
              value={filterMonth}
              onChange={val => setFilterMonth(val)}
              style={{ width: "100%" }}
            />
          </div>

          {/* Branch */}
          <div style={{ flex: "1 1 150px", position: "relative" }}>
            <select
              value={filterBranch}
              onChange={e => setFilterBranch(e.target.value)}
              style={{ width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "var(--radius-default)", color: "var(--input-color)", padding: "8px 32px 8px 12px", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none", appearance: "none", cursor: "pointer" }}
            >
              {[{ value: "", label: "All Branches" }, ...branchList.map(b => ({ value: String(b.branchId), label: b.branch_name }))].map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
            <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "var(--text-muted)", zIndex: 1, pointerEvents: "none" }} />
          </div>

          {/* Department */}
          <div style={{ flex: "1 1 150px", position: "relative" }}>
            <select
              value={filterDept}
              onChange={e => setFilterDept(e.target.value)}
              style={{ width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "var(--radius-default)", color: "var(--input-color)", padding: "8px 32px 8px 12px", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none", appearance: "none", cursor: "pointer" }}
            >
              {[{ value: "", label: "All Departments" }, ...departmentList.map(d => ({ value: String(d.departmentId), label: d.department_name }))].map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "var(--text-muted)", zIndex: 1, pointerEvents: "none" }} />
          </div>

          {/* Employee Type */}
          <div style={{ flex: "1 1 150px", position: "relative" }}>
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              style={{ width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "var(--radius-default)", color: "var(--input-color)", padding: "8px 32px 8px 12px", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none", appearance: "none", cursor: "pointer" }}
            >
              {EMP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "var(--text-muted)", zIndex: 1, pointerEvents: "none" }} />
          </div>

          {/* Search */}
          <div style={{ flex: "2 1 220px", position: "relative" }}>
            <i className="fa-solid fa-search" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--text-muted)", zIndex: 1, pointerEvents: "none" }} />
            <input
              type="text"
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              placeholder="Search by Employee, Role or Department…"
              style={{ width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "var(--radius-default)", color: "var(--input-color)", padding: "8px 12px 8px 38px", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none", boxSizing: "border-box" }}
            />
          </div>

          {/* Clear button — only shown when a filter is active */}
          {hasActiveFilter && (
            <button
              onClick={clearFilters}
              style={{ flexShrink: 0, padding: "8px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500, background: "rgba(239,68,68,0.08)", color: "var(--color-danger)", border: "1px solid rgba(239,68,68,0.25)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
            >
              <i className="fa-solid fa-xmark" style={{ fontSize: 11 }} /> Clear
            </button>
          )}
        </div>
        {/* ── KPI Cards ── */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 18 }}>
          {loading && PS.kpiCards.length === 0 ? (
            <div
              style={{
                gridColumn: "1 / -1",
                padding: 24,
                textAlign: "center",
                color: "var(--text-muted)",
                fontFamily: "var(--font, 'Outfit', sans-serif)",
                fontSize: 13,
              }}
            >
              Loading payroll summary…
            </div>
          ) : PS.kpiCards.length === 0 ? (
            <div
              style={{
                gridColumn: "1 / -1",
                padding: 24,
                textAlign: "center",
                color: "var(--text-muted)",
                fontFamily: "var(--font, 'Outfit', sans-serif)",
                fontSize: 13,
              }}
            >
              No KPI cards returned for this period. Adjust filters or check the dashboard API.
            </div>
          ) : (
            PS.kpiCards.map((k, i) => (
            <div key={i} style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 18, position: "relative", overflow: "hidden", transition: "all 0.25s", cursor: "pointer" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.15)"; e.currentTarget.style.borderColor = "var(--primary)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = ""; e.currentTarget.style.borderColor = ""; }}>
              <div style={{ position: "absolute", right: -20, top: -20, width: 90, height: 90, background: k.glow, borderRadius: "50%", filter: "blur(22px)" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 500, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>
                  <i className={k.icon} style={{ color: k.iconColor, fontSize: 12 }} /> {k.label}
                </div>
                {k.badge ? <Chip label={k.badge} cc={k.badgeColor} /> : <span style={{ minWidth: 8 }} />}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{k.value}</div>
              <div style={{ fontSize: 10, marginTop: 4, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{k.sub}</div>
            </div>
            ))
          )}
        </section>

        {/* ── Trend + Compliance ── */}
        <section style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 22 }}>
          <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Payroll Trend Overview</h3>
              <select style={{ padding: "5px 10px", fontSize: 12, background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: 8, color: "var(--input-color)", fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none" }}>
                <option>Last 6 Months</option><option>YTD</option>
              </select>
            </div>
            <div ref={trendChartRef} style={{ width: "100%", height: 280 }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 22 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginTop: 0, marginBottom: 14, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Compliance Status</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {PS.complianceStatus.map((row, i) => (
                  <div key={i} style={{ background: "var(--pay-surface-2)", border: "1px solid var(--border)", borderRadius: 12, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: "50%", background: row.ibg, display: "flex", alignItems: "center", justifyContent: "center", color: row.ic }}><i className={row.icon} /></div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{row.title}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{row.sub}</div>
                      </div>
                    </div>
                    <Chip label={row.label} cc={row.cc} />
                  </div>                ))}
              </div>
            </div>
            <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 22 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Critical Alerts</h3>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(239,68,68,0.15)", color: "var(--color-danger)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{PS.criticalAlerts.length}</div>
              </div>
              {PS.criticalAlerts.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 10, fontSize: 12, borderLeft: `2px solid ${a.bl}`, paddingLeft: 10, marginBottom: i === 0 ? 12 : 0, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>
                  <i className={a.icon} style={{ color: a.ic, marginTop: 2 }} />
                  <div>
                    <span style={{ fontWeight: 600, display: "block", color: "var(--text-primary)" }}>{a.title}</span>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{a.sub}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Statutory Compliance Cards ── */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 }}>
          {filteredStatutory.map((s, i) => (
            <div key={i} style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 14, borderLeft: `3px solid ${s.bl}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: s.ibg, display: "flex", alignItems: "center", justifyContent: "center", color: s.ic }}><i className={s.icon} style={{ fontSize: 14 }} /></div>
                <div>
                  <h4 style={{ fontWeight: 600, margin: 0, fontSize: 12, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{s.title}</h4>
                  <p style={{ fontSize: 10, margin: 0, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{s.sub}</p>
                </div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{s.amount}</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 9, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{s.due}</span>
                <Chip label={s.status} cc={s.statusColor} />
              </div>
            </div>
          ))}
        </section>

        {/* ── Payout Distribution + Quick Insights ── */}
        <section style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 22 }}>
          <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Payout Distribution Status</h3>
                <p style={{ fontSize: 11, margin: "4px 0 0", color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Real-time payment processing overview</p>
              </div>
              <GhostBtn onClick={() => loadPayrollSummary()} disabled={loading}>
                <i className="fa-solid fa-rotate-right" style={{ fontSize: 11 }} /> Refresh
              </GhostBtn>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
              {PS.payoutDistribution.map((p, i) => (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: p.ibg, display: "flex", alignItems: "center", justifyContent: "center", color: p.ic, fontSize: 13 }}><i className={p.icon} /></div>
                      <div>
                        <div style={{ fontWeight: 500, color: "var(--text-primary)", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{p.label}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{p.sub}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{p.count}</div>
                      <div style={{ fontSize: 11, color: p.ic, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{p.pl}</div>
                    </div>
                  </div>
                  <div style={{ width: "100%", height: 8, background: "var(--pay-surface-2)", borderRadius: 9999, overflow: "hidden" }}>
                    <div style={{ width: `${p.pct}%`, height: "100%", borderRadius: 9999, background: p.grad }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {PS.quickInsights.map((q, i) => (
              <div key={i} style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 18, display: "flex", alignItems: "flex-start", gap: 14, cursor: "pointer" }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: q.grad, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 4px 12px ${q.shadow}` }}>
                  <i className={q.icon} style={{ color: "#fff", fontSize: 12 }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, marginBottom: 4, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{q.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{q.value}</div>
                  <div style={{ fontSize: 11, marginTop: 4, color: q.tc, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{q.trend}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Gross Payroll Cost Breakdown Chart (PIE CHART - MOVED TO TOP) ── */}
        <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Gross Payroll Cost Breakdown</h3>
              <p style={{ fontSize: 11, margin: "4px 0 0", color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Distribution of total payroll components</p>
            </div>
            <select
              disabled
              value={filterMonth}
              style={{ padding: "5px 10px", fontSize: 12, background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: 8, color: "var(--input-color)", fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none", opacity: 0.85 }}
            >
              <option value={filterMonth}>{monthLabel || filterMonth}</option>
            </select>
          </div>
          <div ref={breakdownChartRef} style={{ width: "100%", height: 340 }} />
        </div>

        {/* ══════════════════════════════════════════════════════════
            EMPLOYEE PAYROLL STATUS TABLE (BELOW PIE CHART)
        ══════════════════════════════════════════════════════════ */}
        <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "20px 22px 16px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Employee Payroll Status</h3>
              <p style={{ fontSize: 12, margin: "3px 0 0", color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Detailed view of individual processing states.</p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <GhostBtn><i className="fa-solid fa-filter" style={{ fontSize: 10 }} /> Filter</GhostBtn>
              <GhostBtn><i className="fa-solid fa-columns" style={{ fontSize: 10 }} /> Columns</GhostBtn>
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: "var(--pay-surface-2)" }}>
                  <th style={thStyle()}>Employee</th>
                  <th style={thStyle()}>ID &amp; Role</th>
                  <th style={thStyle()}>Gross Pay</th>
                  <th style={thStyle()}>Deductions</th>
                  <th style={thStyle()}>Net Pay</th>
                  <th style={thStyle()}>Status</th>
                  <th style={thStyle(true)}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ ...tdStyle(), textAlign: "center", padding: "32px 18px", color: "var(--text-muted)" }}>
                      <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 20, marginBottom: 8, display: "block", opacity: 0.4 }} />
                      No employees match the current filters.
                    </td>
                  </tr>
                ) : pagedEmployees.map((row, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.15s" }} onMouseEnter={rowHoverIn} onMouseLeave={rowHoverOut}>
                    <td style={tdStyle()}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {row.avatar
                          ? <img src={row.avatar} alt={row.name} style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(134,96,248,0.2)" }} />
                          : <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--pay-surface-2)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{row.initials}</div>
                        }
                        <span style={{ fontWeight: 600 }}>{row.name}</span>
                      </div>
                    </td>
                    <td style={tdStyle()}>
                      <div style={{ fontWeight: 600 }}>{row.id}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{row.role}</div>
                    </td>
                    <td style={tdStyle({ fontWeight: 600 })}>{row.gross}</td>
                    <td style={tdStyle({ color: "var(--color-danger)", fontWeight: 500 })}>{row.deductions}</td>
                    <td style={tdStyle({ fontWeight: 700 })}>{row.net}</td>
                    <td style={tdStyle()}><Chip label={row.status} cc={row.statusColor} /></td>
                    <td style={tdStyle({ textAlign: "right" })}>
                      <button style={{ background: "none", border: "none", cursor: "pointer", padding: "0 6px", color: "var(--text-muted)" }}><i className="fa-solid fa-eye" style={{ fontSize: 12 }} /></button>
                      <button style={{ background: "none", border: "none", cursor: "pointer", padding: "0 6px", color: "var(--text-muted)" }}><i className="fa-solid fa-ellipsis-vertical" style={{ fontSize: 12 }} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "11px 22px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, fontFamily: "var(--font, 'Outfit', sans-serif)", color: "var(--text-muted)" }}>
            <span>
              {filteredEmployees.length === 0
                ? "No results"
                : `Showing ${empPage * empPageSize + 1}–${Math.min((empPage + 1) * empPageSize, filteredEmployees.length)} of ${filteredEmployees.length} employee${filteredEmployees.length !== 1 ? "s" : ""}`}
            </span>
            <CustomPagination
              total={filteredEmployees.length}
              page={empPage}
              pageSize={empPageSize}
              onPageChange={setEmpPage}
              onPageSizeChange={(s) => { setEmpPageSize(s); setEmpPage(0); }}
            />
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════
            DEPARTMENT-WISE PAYROLL TABLE (BELOW EMPLOYEE TABLE)
        ══════════════════════════════════════════════════════════ */}
        <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "20px 22px 16px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Department-wise Payroll Summary</h3>
              <p style={{ fontSize: 12, margin: "3px 0 0", color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Cost allocation across organizational units.</p>
            </div>
            <GhostBtn><i className="fa-solid fa-download" style={{ fontSize: 10 }} /> Export</GhostBtn>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: "#2d1a5e" }}>
                  <th style={thStyle()}>Department</th>
                  <th style={thStyle()}>Employees</th>
                  <th style={thStyle()}>Gross Payroll</th>
                  <th style={thStyle()}>Benefits</th>
                  <th style={thStyle()}>Deductions</th>
                  <th style={thStyle()}>Net Payroll</th>
                  <th style={thStyle()}>Status</th>
                  <th style={thStyle(true)}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {pagedDepts.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ ...tdStyle(), textAlign: "center", padding: "32px 18px", color: "var(--text-muted)" }}>
                      <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 20, marginBottom: 8, display: "block", opacity: 0.4 }} />
                      No departments match the current filters.
                    </td>
                  </tr>
                ) : pagedDepts.map((d, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.15s" }} onMouseEnter={rowHoverIn} onMouseLeave={rowHoverOut}>
                    <td style={tdStyle()}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: d.ibg, display: "flex", alignItems: "center", justifyContent: "center", color: d.ic, fontSize: 13 }}><i className={d.icon} /></div>
                        <span style={{ fontWeight: 600 }}>{d.name}</span>
                      </div>
                    </td>
                    <td style={tdStyle()}>{d.employeeCount}</td>
                    <td style={tdStyle({ fontWeight: 600 })}>{d.gross}</td>
                    <td style={tdStyle({ color: "var(--color-success)", fontWeight: 500 })}>{d.benefits}</td>
                    <td style={tdStyle({ color: "var(--color-danger)", fontWeight: 500 })}>{d.deductions}</td>
                    <td style={tdStyle({ fontWeight: 700 })}>{d.net}</td>
                    <td style={tdStyle()}><Chip label={d.status} cc={d.statusColor} /></td>
                    <td style={tdStyle({ textAlign: "right", fontWeight: 600 })}>{d.pctOfTotal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "11px 22px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, fontFamily: "var(--font, 'Outfit', sans-serif)", color: "var(--text-muted)" }}>
            <span>
              {filteredDepts.length === 0
                ? "No results"
                : `Showing ${deptPage * deptPageSize + 1}–${Math.min((deptPage + 1) * deptPageSize, filteredDepts.length)} of ${filteredDepts.length} department${filteredDepts.length !== 1 ? "s" : ""}`}
            </span>
            <CustomPagination
              total={filteredDepts.length}
              page={deptPage}
              pageSize={deptPageSize}
              onPageChange={setDeptPage}
              onPageSizeChange={(s) => { setDeptPageSize(s); setDeptPage(0); }}
            />
          </div>
        </div>

      </main>
    </div>
  );
};

export default PayrollSummary;